import type { ReactNode } from 'react'
import type { ColumnProps } from '../types'
import CustomRow from './CustomRow'
import classes from './Table.module.scss'

interface Props {
  ariaLabel?: string
  columns: ColumnProps[]
  isCompact?: boolean
  rowKey: string
  rows: any[]
  expandedRowList?: string[]
  selectedRowList?: string[]
  renderExpandedRowContent?: (rowId: number) => ReactNode
}

const Table = ({
  ariaLabel,
  columns,
  rowKey,
  rows,
  expandedRowList,
  selectedRowList,
  renderExpandedRowContent,
  isCompact = false
}: Props) => {
  const className = isCompact
    ? classes.Table + ' ' + classes.Compact
    : classes.Table

  return (
    <table
      aria-label={ariaLabel}
      className={className}
    >
      <thead>
        <tr>
          {
            columns.map(column => {
              if (column.header?.transforms?.length) {
              // Merge all transform results; `children` becomes the th content,
              // remaining keys become th attributes (e.g. role, scope, aria-sort)
                const merged = column.header.transforms.reduce(
                  (acc, transform) => ({
                    ...acc,
                    ...transform()
                  }),
                {} as Record<string, unknown>
                )

                const {
                  children: thContent,
                  ...thAttrs
                } = merged

                return (
                  <th
                    key={String(column.rowItemKey)}
                    {...thAttrs as object}
                  >
                    {thContent as ReactNode}
                  </th>
                )
              }

              return (
                <th
                  key={String(column.rowItemKey)}
                  {...column.props}
                >
                  {column.label}
                </th>
              )
            })
          }
        </tr>
      </thead>
      {
        rows.length > 0 && (
          <tbody>
            {
              rows.map(rowData => {
                const rowId = rowData[rowKey]

                const hasExpandedChildren =
              renderExpandedRowContent &&
              expandedRowList &&
              expandedRowList.includes(rowId)

                const isSelected = !!(selectedRowList && selectedRowList.includes(rowId))

                return (
                  <CustomRow
                    expandedChildren={
                      hasExpandedChildren
                        ? renderExpandedRowContent(rowId)
                        : undefined
                    }
                    isSelected={isSelected}
                    key={rowId}
                  >
                    {
                      columns.map(column => (
                        <td
                          key={String(column.rowItemKey)}
                          {...column.props}
                        >
                          {rowData[column.rowItemKey]}
                        </td>
                      ))
                    }
                  </CustomRow>
                )
              })
            }
          </tbody>
        )
      }
    </table>
  )
}

export default Table
